/* ==========================================================================
   CBLP Styling - homepage behaviour
   Scroll reveal, stat counters, Ikon DRL switcher, chassis spotlight carousel
   and the FAQ accordion.

   Principles carried over from the design handoff:
   - The hidden pre-reveal state is only applied once JS is running, so a
     no-JS visitor or a script failure sees the content rather than a blank page.
   - Anything already scrolled past is revealed immediately.
   - Everything re-initialises on shopify:section:load for the theme editor.
   - Reduced motion is honoured in JS as well as CSS, because CSS alone cannot
     stop a timer.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ---------------------------------------------------------------- reveal */
  var revealSeen = new WeakSet();
  var revealObserver = null;
  var counterObserver = null;

  function countUp(el, animate) {
    var end = parseFloat(el.dataset.cblpCount);
    var dec = parseInt(el.dataset.cblpDec || '0', 10);
    var suffix = el.dataset.cblpSuffix || '';
    if (isNaN(end)) return;
    if (!animate) {
      el.textContent = end.toFixed(dec) + suffix;
      return;
    }
    var start = performance.now();
    var duration = 1400;
    var frame = function (now) {
      var p = Math.min(1, (now - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = (end * eased).toFixed(dec) + suffix;
      if (p < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  function scanReveal(root) {
    var motion = !reduced();
    var scope = root || document;

    if (motion && !revealObserver && 'IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
              entry.target.classList.add('cblp-in');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px' }
      );
    }
    if (!counterObserver && 'IntersectionObserver' in window) {
      counterObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              countUp(entry.target, !reduced());
              counterObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.4 }
      );
    }

    scope.querySelectorAll('.cblp-rv').forEach(function (el) {
      if (revealSeen.has(el)) return;
      revealSeen.add(el);
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('cblp-in');
    });

    scope.querySelectorAll('[data-cblp-count]').forEach(function (el) {
      if (revealSeen.has(el)) return;
      revealSeen.add(el);
      if (motion) {
        el.textContent = (0).toFixed(parseInt(el.dataset.cblpDec || '0', 10)) + (el.dataset.cblpSuffix || '');
      }
      if (counterObserver) counterObserver.observe(el);
      else countUp(el, false);
    });
  }

  /* ------------------------------------------------------------ Ikon DRL */
  function initIkon(root) {
    (root || document).querySelectorAll('[data-cblp-ikon]').forEach(function (node) {
      if (node.dataset.cblpReady === '1') return;
      node.dataset.cblpReady = '1';

      var line = node.querySelector('[data-cblp-drl-line]');
      var label = node.querySelector('[data-cblp-drl-label]');
      var buttons = Array.prototype.slice.call(node.querySelectorAll('[data-cblp-drl]'));
      var photos = Array.prototype.slice.call(node.querySelectorAll('[data-cblp-drl-photo]'));
      if (!line || !buttons.length) return;

      var timer = null;

      var commit = function (btn) {
        var key = btn.dataset.cblpDrl;
        var photoIndex = parseInt(btn.dataset.cblpPhoto || '0', 10);
        line.className = 'cblp-drl-line cblp-drl-' + key;
        if (label) label.textContent = btn.dataset.cblpLabel || '';
        buttons.forEach(function (b) {
          b.setAttribute('aria-pressed', String(b === btn));
        });
        photos.forEach(function (img, i) {
          var on = i === photoIndex;
          img.classList.toggle('cblp-on', on);
          img.setAttribute('aria-hidden', String(!on));
        });
      };

      var press = function (btn) {
        clearTimeout(timer);
        var key = btn.dataset.cblpDrl;
        var flashes = parseInt(btn.dataset.cblpFlashes || '0', 10);

        buttons.forEach(function (b) {
          b.setAttribute('aria-pressed', String(b === btn));
        });

        if (reduced() || !flashes) {
          commit(btn);
          return;
        }

        // Simulate the high-beam lever: blink N times in the target colour,
        // then settle on it and swap the photograph.
        var step = 0;
        var total = flashes * 2;
        var tick = function () {
          if (step >= total) {
            commit(btn);
            return;
          }
          var lit = step % 2 === 0;
          line.className = 'cblp-drl-line cblp-drl-' + key + (lit ? '' : ' cblp-off');
          step += 1;
          timer = setTimeout(tick, step % 2 ? 110 : 90);
        };
        tick();
      };

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          press(btn);
        });
      });
    });
  }

  /* --------------------------------------------------------- spotlight */
  function initSpotlight(root) {
    (root || document).querySelectorAll('[data-cblp-spotlight]').forEach(function (node) {
      if (node.dataset.cblpReady === '1') return;
      node.dataset.cblpReady = '1';

      var images = Array.prototype.slice.call(node.querySelectorAll('[data-cblp-spot-img]'));
      var copies = Array.prototype.slice.call(node.querySelectorAll('[data-cblp-spot-copy]'));
      var bars = Array.prototype.slice.call(node.querySelectorAll('[data-cblp-spot-bar]'));
      var prev = node.querySelector('[data-cblp-spot-prev]');
      var next = node.querySelector('[data-cblp-spot-next]');
      var count = copies.length;
      if (count < 1) return;

      var index = 0;
      var auto = !reduced() && count > 1;
      var timer = null;

      var render = function () {
        images.forEach(function (img, i) {
          img.classList.toggle('cblp-on', i === index);
          img.setAttribute('aria-hidden', String(i !== index));
        });
        copies.forEach(function (copy, i) {
          copy.classList.toggle('cblp-on', i === index);
          copy.setAttribute('aria-hidden', String(i !== index));
        });
        bars.forEach(function (bar, i) {
          bar.classList.remove('cblp-done', 'cblp-run');
          if (i < index || (i === index && !auto)) bar.classList.add('cblp-done');
          else if (i === index) bar.classList.add('cblp-run');
          bar.setAttribute('aria-selected', String(i === index));
        });
      };

      var go = function (i, manual) {
        index = ((i % count) + count) % count;
        if (manual) {
          auto = false;
          clearInterval(timer);
        }
        render();
      };

      bars.forEach(function (bar, i) {
        bar.addEventListener('click', function () {
          go(i, true);
        });
      });
      if (prev)
        prev.addEventListener('click', function () {
          go(index - 1, true);
        });
      if (next)
        next.addEventListener('click', function () {
          go(index + 1, true);
        });

      render();
      if (auto) {
        timer = setInterval(function () {
          if (!auto) return clearInterval(timer);
          index = (index + 1) % count;
          render();
        }, 6000);
      }
    });
  }

  /* --------------------------------------------------------------- FAQ */
  function initFaq(root) {
    (root || document).querySelectorAll('[data-cblp-faq]').forEach(function (node) {
      if (node.dataset.cblpReady === '1') return;
      node.dataset.cblpReady = '1';

      var items = Array.prototype.slice.call(node.querySelectorAll('[data-cblp-faq-item]'));
      items.forEach(function (item, i) {
        var button = item.querySelector('button');
        if (!button) return;
        button.addEventListener('click', function () {
          var isOpen = item.getAttribute('data-open') === 'true';
          items.forEach(function (other, j) {
            var open = !isOpen && j === i;
            other.setAttribute('data-open', String(open));
            var b = other.querySelector('button');
            if (b) b.setAttribute('aria-expanded', String(open));
          });
        });
      });
    });
  }

  /* ------------------------------------------------------------- boot */
  function boot(root) {
    scanReveal(root);
    initIkon(root);
    initSpotlight(root);
    initFaq(root);
  }

  function start() {
    // Only now, with JS confirmed running, is the pre-reveal hidden state armed.
    if (!reduced()) document.documentElement.classList.add('cblp-rv-on');
    boot(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: sections are injected and replaced without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
  document.addEventListener('shopify:section:select', function (event) {
    boot(event.target);
  });
})();
